import { useEffect, useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { photoUrl, photoAlt } from "@/lib/photos";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

const SECTION_LABELS = {
  portada: { es: "Portada", en: "Cover" },
  sala: { es: "Sala", en: "Living" },
  cocina: { es: "Cocina", en: "Kitchen" },
  comedor: { es: "Comedor", en: "Dining" },
  recamara_1: { es: "Recámara 1", en: "Bedroom 1" },
  recamara_2: { es: "Recámara 2", en: "Bedroom 2" },
  recamara_3: { es: "Recámara 3", en: "Bedroom 3" },
  bano_1: { es: "Baño 1", en: "Bathroom 1" },
  bano_2: { es: "Baño 2", en: "Bathroom 2" },
  exterior: { es: "Exterior", en: "Exterior" },
  playa: { es: "Playa", en: "Beach" },
  experiencias: { es: "Experiencias", en: "Experiences" },
};

// Order in which sections appear (experiencias is excluded from gallery — it's used in Experiences component)
const DISPLAY_ORDER = [
  "sala",
  "cocina",
  "comedor",
  "recamara_1",
  "recamara_2",
  "recamara_3",
  "bano_1",
  "bano_2",
  "exterior",
  "playa",
];

function SectionCarousel({ title, count, photos }) {
  return (
    <div data-testid={`gallery-section-${title}`} className="mb-12 last:mb-0">
      <div className="flex items-baseline justify-between border-b border-stone-300 pb-3 mb-6">
        <h3 className="font-serif-display text-2xl md:text-3xl text-stone-900">{title}</h3>
        <span className="text-eyebrow text-stone-500">
          {count} {count === 1 ? "foto" : "fotos"}
        </span>
      </div>
      <Carousel opts={{ align: "start", loop: photos.length > 1 }} className="w-full">
        <CarouselContent className="-ml-3 md:-ml-4">
          {photos.map((p) => (
            <CarouselItem
              key={p.id}
              data-testid={`carousel-item-${p.id}`}
              className="pl-3 md:pl-4 basis-full sm:basis-1/2 lg:basis-1/3"
            >
              <div className="aspect-[4/3] overflow-hidden bg-stone-200 group">
                <img
                  src={photoUrl(p)}
                  alt={photoAlt(p, p.original_filename || "")}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms] ease-out"
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        {photos.length > 1 && (
          <>
            <CarouselPrevious
              data-testid={`carousel-prev-${title}`}
              className="left-0 -translate-x-2 md:-translate-x-4 rounded-none bg-stone-900 border-stone-900 text-stone-50 hover:bg-[#E07A5F] hover:border-[#E07A5F] hover:text-white"
            />
            <CarouselNext
              data-testid={`carousel-next-${title}`}
              className="right-0 translate-x-2 md:translate-x-4 rounded-none bg-stone-900 border-stone-900 text-stone-50 hover:bg-[#E07A5F] hover:border-[#E07A5F] hover:text-white"
            />
          </>
        )}
      </Carousel>
    </div>
  );
}

export default function Gallery({ property }) {
  const { t, lang } = useLang();
  const [sections, setSections] = useState(null);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/photos?property_id=${property.id}`);
        if (cancelled) return;
        setSections(data.sections || {});
        // total excluding 'experiencias' and 'portada' (not shown in gallery)
        const visible = Object.entries(data.sections || {})
          .filter(([k]) => k !== "experiencias" && k !== "portada")
          .reduce((acc, [, v]) => acc + (v?.length || 0), 0);
        setTotal(visible);
      } catch {
        if (!cancelled) {
          setSections({});
          setTotal(0);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [property.id]);

  const useDynamic = total > 0;

  return (
    <section id="galeria" data-testid="gallery-section" className="bg-stone-100 py-24 md:py-40">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-6">
          <div>
            <p className="text-eyebrow text-[#E07A5F] mb-4">{t("gallery.eyebrow")}</p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-stone-900 max-w-2xl">
              {t("gallery.title")}
            </h2>
          </div>
        </div>

        {useDynamic ? (
          <div>
            {DISPLAY_ORDER.map((sectionId) => {
              const photos = sections?.[sectionId] || [];
              if (!photos.length) return null;
              const label = SECTION_LABELS[sectionId]?.[lang] || sectionId;
              return (
                <SectionCarousel
                  key={sectionId}
                  title={label}
                  count={photos.length}
                  photos={photos}
                />
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-[280px] md:auto-rows-[260px] gap-3 md:gap-4">
            {property.gallery.map((img, i) => (
              <div
                key={i}
                data-testid={`gallery-img-${i}`}
                className={`${img.span} overflow-hidden bg-stone-200 relative group`}
              >
                <img
                  src={img.url}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms] ease-out"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
